import React, { ReactElement } from 'react';

import { useDialogState } from '../../../../../../../../../common/hooks/useDialogState';
import useCheckInstitutionalDuplicateAccess from '../../../../../../../../../common/util/check-access/useCheckInstitutionalDuplicateAccess';
import {
  IndicationStatus,
  InstitutionalIndicationOrderType,
} from '../../../../../../../../../graphql';
import { useIndicationActivitySyndicateContext } from '../../../../../context/IndicationActivitySyndicateContext';
import { type Props as ReadButtonProps } from '../../../common/indication-section-read-view/ReadButtons';
import { PassIndicationDialog } from '../../../common/PassIndicationDialog';
import { usePassOnInstitutionalIndication } from '../../common/pass-institutional-indication-dialog/hooks/usePassOnInstitutionalIndication';
import { useDeleteInstitutionalIndication } from '../../hooks/useDeleteInstitutionalIndication';
import CancelIndicationConfirmDialog from '../cancel-indication-confirm-dialog/CancelIndicationConfirmDialog';
import DeleteIndicationConfirmDialog from '../delete-indication-confirm-dialog/DeleteIndicationConfirmDialog';
import { useUpdateIndicationStatus } from './useUpdateIndicationStatus';

export type Params = Readonly<{
  isIndicationEditable: boolean;
  onStartEditing: () => void;
}>;

const useCreateSyndicateReadButtonProps = ({
  isIndicationEditable,
  onStartEditing,
}: Params): ReadButtonProps & { readButtonDialogs: ReactElement[] } => {
  const {
    offeringId,
    access: { canUpdateIndication },
    basicPublishedOffering,
  } = useIndicationActivitySyndicateContext();
  const { canManageDuplicate } = useCheckInstitutionalDuplicateAccess({ offeringId });

  const indication = basicPublishedOffering!.orderBook.institutionalIndication!;
  const investorName = indication.investorInformation.bankInvestorName ?? '';
  const isPartOfDuplicateGroup =
    indication.status === IndicationStatus.Duplicate ||
    indication.duplicateIndicationIds.length > 0;

  const {
    isOpen: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDialogState();
  const {
    isOpen: isPassDialogOpen,
    onOpen: openPassDialog,
    onClose: closePassDialog,
  } = useDialogState();
  const {
    isOpen: isCancelDialogOpen,
    onOpen: openCancelDialog,
    onClose: closeCancelDialog,
  } = useDialogState();

  const [onUpdateStatus, isUpdatingStatus] = useUpdateIndicationStatus(offeringId, indication.id);
  const [onPass, isPassing] = usePassOnInstitutionalIndication({
    offeringId,
    indicationId: indication.id,
    indicationStatus: indication.status,
    onClose: closePassDialog,
  });
  const [onDelete, isDeleting] = useDeleteInstitutionalIndication(indication.id, investorName);

  return {
    baseButtonAccess: isIndicationEditable && canUpdateIndication,
    isVisible: {
      edit: indication.status !== IndicationStatus.Cancelled,
      pass: indication.type !== InstitutionalIndicationOrderType.Pass,
      reactivate: indication.status === IndicationStatus.Cancelled,
      cancel: indication.status !== IndicationStatus.Cancelled,
      delete: !isPartOfDuplicateGroup || canManageDuplicate,
    },
    editButtonProps: {
      disabled: isUpdatingStatus,
      onClick: onStartEditing,
    },
    menuItemProps: {
      pass: {
        disabled: isUpdatingStatus,
        onClick: openPassDialog,
      },
      reactivate: {
        disabled: isUpdatingStatus,
        onClick: () => onUpdateStatus(IndicationStatus.Active),
      },
      cancel: {
        onClick: openCancelDialog,
      },
      delete: {
        tooltipProps: {
          variant: 'info',
          title: isPartOfDuplicateGroup
            ? 'IOI must be removed from duplicate group before deleting'
            : '',
        },
        disabled: isDeleting || isPartOfDuplicateGroup,
        onClick: openDeleteDialog,
      },
    },
    readButtonDialogs: [
      <CancelIndicationConfirmDialog
        indicationId={indication.id}
        offeringId={offeringId}
        onClose={closeCancelDialog}
        isOpen={isCancelDialogOpen}
      />,
      <PassIndicationDialog
        isOpen={isPassDialogOpen}
        isLoading={isPassing}
        onClose={closePassDialog}
        onPass={onPass}
      />,
      <DeleteIndicationConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        isDeleting={isDeleting}
        onDelete={onDelete}
        investorName={investorName}
      />,
    ],
  };
};

export default useCreateSyndicateReadButtonProps;
